<template>
  <SfInpageListItem
    :selected="selected"
    :class="{ 'period-selection-item--selected': selected }"
    extra-list-item-class="tw-py-3"
    :icon-size="40"
    @item-click="itemClick"
  >
    <template #icon>
      <img
        v-tooltip.html.placementBottom="
          I18n.t(periodSelectionOption.tooltipKey)
        "
        data-tooltip-size="sm"
        :src="$imagesRequire(iconPath)"
        class="tw-w-6 tw-h-6 tw-flex"
      />
    </template>

    <template #title>
      <PeriodSelectionDateTypeDropdown
        v-if="showDateTypeDropdown"
        :dropdown-id="dropdownId"
        :period-selection-option="periodSelectionOption"
        :ledger-select-date="ledgerSelectDate"
        :selected-date-type="ledgerSelectDateType"
        :is-open="isScopedDropdownOpen"
        :is-parent-selected="selected"
        @date-type-selected="handleDateTypeSelected"
        @date-selected="handleDateSelected"
        @toggle-dropdown="handleToggleDropdown"
      />

      <PeriodSelectionLockTypeDropdown
        v-if="showLockLockTypeDropdown"
        :dropdown-id="dropdownId"
        :period-selection-option="periodSelectionOption"
        :selected-lock-type="ledgerSelectLockType"
        :is-open="isScopedDropdownOpen"
        :is-parent-selected="selected"
        @lock-type-selected="handleLockTypeSelected"
        @toggle-dropdown="handleToggleDropdown"
      />
    </template>

    <template v-if="periodSelectionOption.subtitleKey" #subtitles>
      <SfInpageListItemSubtitle>
        {{ I18n.t(periodSelectionOption.subtitleKey) }}
      </SfInpageListItemSubtitle>
    </template>

    <template #right>
      <img
        v-if="selected"
        v-tooltip.html.placementBottom="
          I18n.t(periodSelectionOption.tooltipKey)
        "
        data-tooltip-size="sm"
        :src="$imagesRequire(illustrationPath)"
      />

      <div
        v-if="selected"
        class="tw-bg-sf-neutral-lighter-2 tw-w-[1px] tw-h-4 tw-mx-5"
      >
        &nbsp;
      </div>

      <label class="sf-form-check sf-form sf-form-check-rounded">
        <input
          :id="inputId"
          :checked="selected"
          name="period-selection"
          type="radio"
        />
        <div class="sf-form-check-content">
          <span class="sf-level-item sf-form-check-indicator" />
        </div>
      </label>
    </template>
  </SfInpageListItem>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { storeToRefs } from 'pinia'
import { useDropdown } from '@app/shared/pinia/dropdown'
import SfInpageListItem from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item.vue'
import SfInpageListItemSubtitle from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item-subtitle.vue'
import PeriodSelectionLockTypeDropdown from './period-selection-lock-type-dropdown.vue'
import PeriodSelectionDateTypeDropdown from './period-selection-date-type-dropdown.vue'
import type { DateType, LockType, PeriodSelectionOption } from '../types'
import { dateTypes, periodSelectionTypes } from '../types'

interface Props {
  periodSelectionOption: PeriodSelectionOption
  ledgerSelectDate: string | null
  ledgerSelectLockType: LockType | undefined
  ledgerSelectDateType: DateType | undefined
  selected: boolean
}

const props = defineProps<Props>()
const emit =
  defineEmits<{
    'item-click': [key: string]
    'date-type-selected': [dateType: DateType | undefined]
    'date-selected': [date: string]
    'lock-type-selected': [lockType: LockType]
  }>()

const I18n = useI18n()
const dropdownStore = useDropdown()
const { currentDropdownId } = storeToRefs(dropdownStore)
const dropdownId = 'period-selection-item-dropdown'

onMounted(() => {
  dropdownStore.closeDropdown()
})

const isScopedDropdownOpen = computed(() => {
  return props.selected && currentDropdownId.value === dropdownId
})

const iconPathSuffix = computed(() => (props.selected ? 'active' : 'inactive'))
const iconPath = computed(
  () =>
    `insights/${props.periodSelectionOption.icon}-${iconPathSuffix.value}.svg`
)

const illustrationPath = computed(
  () => `insights/${props.periodSelectionOption.illustration}.svg`
)

const showLockLockTypeDropdown = computed(() => {
  return (
    props.periodSelectionOption.key === periodSelectionTypes.ACTIVE_PERIOD ||
    props.periodSelectionOption.key === periodSelectionTypes.COMPLETED_PERIOD
  )
})

const showDateTypeDropdown = computed(() => {
  return (
    props.periodSelectionOption.key === periodSelectionTypes.FINANCIAL_YEAR ||
    props.periodSelectionOption.key === periodSelectionTypes.ACCOUNTING_PERIOD
  )
})

const inputId = computed(
  () => `period_selection_${props.periodSelectionOption.key}`
)

const itemClick = () => {
  emit('item-click', props.periodSelectionOption.key)
  dropdownStore.closeDropdown()
}

const handleToggleDropdown = () => {
  if (props.selected) {
    dropdownStore.toggleDropdown(dropdownId)
  } else {
    itemClick()
  }
}

const handleLockTypeSelected = (lockType: LockType) => {
  emit('lock-type-selected', lockType)
  dropdownStore.closeDropdown()
}

const handleDateTypeSelected = (dateType: DateType) => {
  emit('date-type-selected', dateType)

  if (dateType === dateTypes.TODAY) {
    dropdownStore.closeDropdown()
  }
}

const handleDateSelected = (date: string) => {
  emit('date-selected', date)
  dropdownStore.closeDropdown()
}
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

:deep(.sf-dropdown2-wrapper::after) {
  opacity: 0;
}

.period-selection-item--selected {
  :deep(.period-selection-item__title .dropdown-trigger),
  :deep(.sf-dropdown2-wrapper::after) {
    @include color('primary', 'dark');

    opacity: 1;
  }
}

:deep(.sf-dropdown2-select) {
  padding: 0;
  padding-right: 28px;
}
</style>
